import React, { useState } from 'react';

const Home = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [suggestions, setSuggestions] = useState([]);

  const calculators = [
    { title: 'Biology', link: '/biology-calculators' },
    { title: 'Chemistry', link: '/chemistry-calculators' },
    { title: 'Construction', link: '/construction-calculators' },
    { title: 'Conversion', link: '/calculator/conversion' },
    { title: 'Everyday Life', link: '/everyday-calculators' },
    { title: 'Finance', link: '/finance-calculators' },
    { title: 'Food', link: '/calculator/food' },
    { title: 'Math', link: '/math-calculators' },
    { title: 'Physics', link: '/physics-calculators' },
    { title: 'Statistics', link: '/statistics-calculators' },
    { title: 'Numerology', link: '/neomology-calculators' },
    { title: 'Other', link: '/other-calculators' },
  ];

  const handleSearchChange = (event) => {
    const value = event.target.value;
    setSearchTerm(value);
    if (value) {
      const filteredSuggestions = calculators.filter(calculator =>
        calculator.title.toLowerCase().includes(value.toLowerCase())
      );
      setSuggestions(filteredSuggestions);
    } else {
      setSuggestions([]);
    }
  };

  return (
    <div className="relative overflow-hidden pb-16">
      {/* Background Gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-cyan-50/80 via-blue-50/80 to-indigo-50/80 backdrop-blur-sm" />
      <div className="absolute inset-0">
        <div className="absolute top-0 left-0 w-96 h-96 bg-blue-200/20 rounded-full mix-blend-multiply filter blur-3xl animate-blob" />
        <div className="absolute top-0 right-0 w-96 h-96 bg-cyan-200/20 rounded-full mix-blend-multiply filter blur-3xl animate-blob animation-delay-2000" />
        <div className="absolute -bottom-8 left-20 w-96 h-96 bg-indigo-200/20 rounded-full mix-blend-multiply filter blur-3xl animate-blob animation-delay-4000" />
        <div className="absolute -bottom-8 right-20 w-96 h-96 bg-sky-200/20 rounded-full mix-blend-multiply filter blur-3xl animate-blob animation-delay-6000" />
        
        {/* Grid pattern overlay */}
        <div className="absolute inset-0 bg-grid-blue/[0.08] bg-[length:20px_20px]" />
      </div>

      {/* Main content */}
      <div className="relative py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 gap-12 lg:gap-16">
            {/* Search Container */}
            <div className="relative bg-white/70 backdrop-blur-lg p-4 rounded-3xl shadow-2xl flex items-center max-w-full mx-auto ring-1 ring-blue-100 hover:shadow-blue-100/20 transition-all duration-300 animate-fade-in">
              <input
                type="text"
                className="flex-1 px-4 py-2 text-lg border border-blue-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white/50 backdrop-blur-sm"
                placeholder="Search for calculators..."
                value={searchTerm}
                onChange={handleSearchChange}
              />
              <button className="bg-blue-600 text-white px-4 py-2 ml-2 rounded-2xl hover:scale-110 hover:rotate-1 hover:shadow-xl hover:shadow-blue-500/40 transition-all duration-300 border border-blue-500/30 hover:border-blue-400 group relative overflow-hidden">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </button>
              {/* Dropdown Arrow */}
              <div className="absolute right-2 top-1/2 transform -translate-y-1/2">
                <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 9l6 6 6-6" />
                </svg>
              </div>
            </div>

            {suggestions.length > 0 && (
              <ul className="absolute z-10 bg-white border border-gray-300 rounded-md mt-1 w-full">
                {suggestions.map((suggestion, index) => (
                  <li key={index} className="p-2 hover:bg-gray-100 cursor-pointer" onClick={() => window.location.href = suggestion.link}>
                    {suggestion.title}
                  </li>
                ))}
              </ul>
            )}

            {/* Calculator Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {[
                { 
                  icon: 'biology',
                  title: 'Biology',
                  count: 42,
                  link: '/biology-calculators',
                  color: 'from-cyan-500 to-blue-500',
                  gradient: 'from-cyan-50 to-blue-50',
                  svg: (
                    <path d="M9 3.6c-1.2.5-2.3 1.2-3.3 2.1 1 .9 1.8 1.9 2.4 3.1.6 1.2.9 2.5.9 3.8 0 1.3-.3 2.6-.9 3.8.6 1.2 1.4 2.2 2.4 3.1 1 .9 2.1 1.6 3.3 2.1 1.2.5 2.5.8 3.8.8 1.3 0 2.6-.3 3.8-.8 1.2-.5 2.3-1.2 3.3-2.1-1-.9-1.8-1.9-2.4-3.1-.6-1.2-.9-2.5-.9-3.8 0-1.3.3-2.6.9-3.8.6-1.2 1.4-2.2 2.4-3.1-1-.9-2.1-1.6-3.3-2.1-1.2-.5-2.5-.8-3.8-.8-1.3 0-2.6.3-3.8.8z" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round"/>
                  )
                },
                { 
                  icon: 'chemistry',
                  title: 'Chemistry',
                  count: 7,
                  link: '/chemistry-calculators',
                  color: 'from-indigo-500 to-blue-500',
                  gradient: 'from-indigo-50 to-blue-50',
                  svg: (
                    <path d="M9 3v4.5m6-4.5v4.5m-6 0v3.5c0 1.1-.9 2-2 2H4m11-5.5v3.5c0 1.1.9 2 2 2h3m-16 4h16M7 21h10" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round"/>
                  )
                },
                { 
                  icon: 'construction',
                  title: 'Construction',
                  count: 6,
                  link: '/construction-calculators',
                  color: 'from-sky-500 to-blue-500',
                  gradient: 'from-sky-50 to-blue-50',
                  svg: (
                    <path d="M15 4V3m0 1v1m0-1h-1m1 0h1m-3.5 3.5L15 7m-3.5 3.5l1-1m-1 1l-1-1m1 1l1 1m-6-1l1.41-1.41M4.5 10.5l1-1m-1 1l-1-1m1-1l1-1m0-6l-1.41 1.41M5.5 4.5l1 1m-1-1l-1 1m1-1l1-1m3 13v-3m0 0l-2-2m2 2l2-2" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round"/>
                  )
                },
                { 
                  icon: 'conversion',
                  title: 'Conversion',
                  count: 36,
                  link: '/calculator/conversion',
                  color: 'from-blue-500 to-indigo-500',
                  gradient: 'from-blue-50 to-indigo-50',
                  svg: (
                    <path d="M8 7h12m0 0l-4-4m4 4l-4 4m-4 6H4m0 0l4 4m-4-4l4-4m4-4l4 4m4 4l-4-4m4-4l-4-4m-4-4l4 4m-4 4l4 4" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round"/>
                  )
                },
                // { 
                //   icon: 'ecology',
                //   title: 'Ecology',
                //   count: 85,
                //   link: '/ecology-calculators',
                //   color: 'from-indigo-500 to-purple-500',
                //   gradient: 'from-indigo-50 to-purple-50',
                //   svg: (
                //     <path d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round"/>
                //   )
                // },
                { 
                  icon: 'everyday',
                  title: 'Everyday Life',
                  count: 30,
                  link: '/everyday-calculators',
                  color: 'from-purple-500 to-fuchsia-500',
                  gradient: 'from-purple-50 to-fuchsia-50',
                  svg: (
                    <path d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round"/>
                  )
                },
                { 
                  icon: 'finance',
                  title: 'Finance',
                  count: 73,
                  link: '/finance-calculators',
                  color: 'from-fuchsia-500 to-purple-500',
                  gradient: 'from-fuchsia-50 to-purple-50',
                  svg: (
                    <path d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round"/>
                  )
                },
                { 
                  icon: 'food',
                  title: 'Food',
                  count: 11,
                  link: '/calculator/food',
                  color: 'from-violet-500 to-purple-500',
                  gradient: 'from-violet-50 to-purple-50',
                  svg: (
                    <path d="M3 3v18h18m-9-9a6 6 0 116 6h-12a6 6 0 016-6zM7 8l2-2m-2 2l-2-2m12 2l2-2m-2 2l-2-2" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round"/>
                  )
                },
                // { 
                //   icon: 'health',
                //   title: 'Health',
                //   count: 31,
                //   link: '/health-calculators',
                //   color: 'from-purple-500 to-pink-500',
                //   gradient: 'from-purple-50 to-pink-50',
                //   svg: (
                //     <path d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round"/>
                //   )
                // },
                { 
                  icon: 'math',
                  title: 'Math',
                  count: 31,
                  link: '/math-calculators',
                  color: 'from-pink-500 to-rose-500',
                  gradient: 'from-pink-50 to-rose-50',
                  svg: (
                    <path d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 102-2h2a2 2 0 002 2v10a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round"/>
                  )
                },
                { 
                  icon: 'physics',
                  title: 'Physics',
                  count: 30,
                  link: '/physics-calculators',
                  color: 'from-rose-500 to-red-500',
                  gradient: 'from-rose-50 to-red-50',
                  svg: (
                    <path d="M12 8c8 0 8 8 8 8s-8 8-8 8-8-8-8-8 8-8 8-8zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round"/>
                  )
                },
                { 
                  icon: 'statistics',
                  title: 'Statistics',
                  count: 30,
                  link: '/statistics-calculators',
                  color: 'from-red-500 to-orange-500',
                  gradient: 'from-red-50 to-orange-50',
                  svg: (
                    <path d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round"/>
                  )
                },
                { 
                  icon: 'numerology',
                  title: 'Numerology',
                  count: 69,
                  link: '/neomology-calculators',
                  color: 'from-blue-500 to-cyan-500',
                  gradient: 'from-blue-50 to-cyan-50',
                  svg: (
                    <path d="M12 20l-4-4m-4 4l4-4m-4-4l4 4m4-4l4 4m4 4l-4-4m4-4l-4-4m-4-4l4 4m-4 4l4 4" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round"/>
                  )
                },
                { 
                  icon: 'other',
                  title: 'Other',
                  count: 82,
                  link: '/other-calculators',
                  color: 'from-orange-500 to-amber-500',
                  gradient: 'from-orange-50 to-amber-50',
                  svg: (
                    <path d="M5 12h.01M12 12h.01M19 12h.01M6 12a1 1 0 11-2 0 1 1 0 012 0zm7 0a1 1 0 11-2 0 1 1 0 012 0zm7 0a1 1 0 11-2 0 1 1 0 012 0z" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round"/>
                  )
                },
               
              ].map((category, index) => (
                <div
                  key={index}
                  onClick={() => window.location.href = category.link}
                  className="group relative bg-white/30 backdrop-blur-sm rounded-3xl overflow-hidden shadow-xl transition-all duration-500 hover:scale-105 hover:shadow-2xl hover:shadow-blue-500/10 animate-fade-in"
                  style={{ animationDelay: `${index * 100}ms`, padding: '1rem' }}
                >
                  <div className={`absolute inset-0 bg-gradient-to-br ${category.color} opacity-10 group-hover:opacity-20 transition-opacity duration-500`}></div>
                  <div className="relative p-4">
                    <div className={`flex items-center justify-center w-12 h-12 mx-auto mb-4 transform group-hover:scale-110 group-hover:rotate-3 transition-all duration-500`}>
                      <svg 
                        className={`w-8 h-8`}
                        viewBox="0 0 24 24" 
                        fill="none" 
                        stroke={`url(#gradient-${index})`} 
                        strokeWidth={1.5}
                      >
                        <defs>
                          <linearGradient id={`gradient-${index}`} x1="0%" y1="0%" x2="100%" y2="100%">
                            <stop offset="0%" className={`text-${category.color.split('-')[2]}-500`} />
                            <stop offset="100%" className={`text-${category.color.split('-')[4]}-500`} />
                          </linearGradient>
                        </defs>
                        {category.svg}
                      </svg>
                    </div>
                    <h3 className="text-lg font-bold mb-1 text-gray-800 group-hover:text-blue-600 transition-colors duration-300 text-center">
                      {category.title}
                    </h3>
                    <p className="text-gray-600 text-center group-hover:text-blue-500 transition-colors duration-300">
                      {category.count} calculators
                    </p>
                    <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-blue-500 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Home;